import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { twMerge } from "tailwind-merge";

import { getNavigationList } from "@/features/analysis/layouts/data/navigation-list";
import { useLang } from "@/hooks/use-lang";

type Props = {
  symbol: string;
  onLinkClick: () => void;
};

export const NavigationClosure = ({ symbol, onLinkClick }: Props) => {
  const { lang, t } = useLang();
  const pathname = usePathname();

  const list = getNavigationList({
    lang,
    t,
    symbol,
  });

  return (
    <ul>
      {list.map(({ title, pageLink, subLinks }) => {
        const hasActiveChild = subLinks.some(
          ({ pageLink: subPageLink }) => pathname === subPageLink
        );
        const isParentActive = pathname === pageLink;
        const shouldOpen = hasActiveChild || isParentActive;

        return (
          <Disclosure as="li" key={title} defaultOpen={shouldOpen}>
            {({ open }) => (
              <>
                <Disclosure.Button
                  as={pageLink ? Link : "div"}
                  className={twMerge(
                    "flex items-center gap-3 rounded-lg p-3 font-semibold text-gray-600",
                    isParentActive && "bg-gray-50 text-sky-700"
                  )}
                  onClick={() => pageLink && onLinkClick()}
                  {...(pageLink && { href: pageLink })}
                >
                  <ChevronRightIcon
                    className={twMerge(
                      "h-5 w-5 shrink-0 transition-transform duration-200",
                      subLinks.length === 0 && "opacity-0",
                      open && "rotate-90"
                    )}
                  />
                  {title}
                </Disclosure.Button>
                <Disclosure.Panel
                  as="ul"
                  className={twMerge(
                    "flex flex-col gap-1 py-1",
                    subLinks.length === 0 && "hidden"
                  )}
                >
                  {subLinks.map(({ name, pageLink: subPageLink }) => {
                    const isChildActive = pathname === subPageLink;
                    return (
                      <li key={name}>
                        <Link
                          href={subPageLink}
                          className={twMerge(
                            "block rounded-lg px-11 py-3 text-gray-600",
                            isChildActive &&
                              "bg-gray-50 font-semibold text-sky-700"
                          )}
                          aria-current={isChildActive}
                          onClick={() => {
                            onLinkClick();
                          }}
                        >
                          {name}
                        </Link>
                      </li>
                    );
                  })}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        );
      })}
    </ul>
  );
};
